import {
  isRuleGraphPipelineKind,
  type RuleGraphDto,
  type RuleGraphEdge,
} from "@intelligems/ig-types";
import { edgeOverlayId } from "./types";

export interface RuleGraphAnalysis {
  graph: RuleGraphDto;
  issues: string[];
  mode: "branched" | "branchless" | "base";
  /** A placement order is not an execution order when a branch forks. */
  canAnimate: boolean;
  laneOrder: string[];
  priorityByBranchId: Map<string, number>;
  pipelineByBranchId: Map<string, string[]>;
  fallbackByBranchId: Map<string, RuleGraphEdge>;
  leadInNodeIds: string[];
  tailNodeIds: string[];
}

/** Presentation analysis only. Never changes the stored graph or infers code behavior. */
export function analyzeRuleGraph(input: RuleGraphDto): RuleGraphAnalysis {
  const issues: string[] = [];
  const nodesById = new Map<string, RuleGraphDto["nodes"][number]>();
  for (const node of input.nodes) {
    if (nodesById.has(node.id)) {
      issues.push(`Duplicate node: ${node.id}`);
    } else {
      nodesById.set(node.id, node);
    }
  }
  const seenEdges = new Set<string>();
  const edges = input.edges.filter((edge) => {
    if (!nodesById.has(edge.from) || !nodesById.has(edge.to)) {
      issues.push(`Missing connection endpoint: ${edge.from} → ${edge.to}`);
      return false;
    }
    const key = edgeOverlayId(edge);
    if (seenEdges.has(key)) {
      issues.push(`Duplicate connection: ${key}`);
      return false;
    }
    seenEdges.add(key);
    return true;
  });
  const graph: RuleGraphDto = {
    ...input,
    nodes: [...nodesById.values()],
    edges,
  };
  const base: RuleGraphAnalysis = {
    graph,
    issues,
    mode: "base",
    canAnimate: false,
    laneOrder: [],
    priorityByBranchId: new Map(),
    pipelineByBranchId: new Map(),
    fallbackByBranchId: new Map(),
    leadInNodeIds: [],
    tailNodeIds: [],
  };
  const anchors = (kind: string) =>
    graph.nodes.filter((node) => node.kind === kind);
  const inputs = anchors("input"),
    outputs = anchors("output"),
    terminals = anchors("fallback-terminal");
  const start = inputs[0],
    output = outputs[0],
    terminal = terminals[0];
  if (
    inputs.length !== 1 ||
    outputs.length !== 1 ||
    terminals.length !== 1 ||
    !start ||
    !output ||
    !terminal
  ) {
    return base;
  }
  if (
    edges.some(
      (edge) =>
        edge.to === start.id ||
        edge.from === output.id ||
        edge.from === terminal.id
    )
  ) {
    return base;
  }
  const flow = edges.filter((edge) => edge.kind === "flow");
  const fallbacks = edges.filter((edge) => edge.kind === "fallback");
  const targets = (id: string) =>
    flow.filter((edge) => edge.from === id).map((edge) => edge.to);
  const pipeline = graph.nodes.filter((node) =>
    isRuleGraphPipelineKind(node.kind)
  );
  for (const node of pipeline) {
    if (
      node.branchId !== undefined &&
      nodesById.get(node.branchId)?.kind !== "branch"
    ) {
      return base;
    }
  }
  if (
    fallbacks.some(
      (edge) =>
        !["branch", "fallback-terminal"].includes(
          nodesById.get(edge.to)?.kind ?? ""
        )
    )
  ) {
    return base;
  }

  // Shared lead-ins and tails must be linear. Branch interiors are analyzed
  // separately so a local fork does not discard the outer branch groups.
  const walk = (first: string[], allowed: Set<string>) => {
    const ordered: string[] = [];
    let next = first;
    while (next.length === 1 && allowed.has(next[0]!)) {
      const id = next[0]!;
      if (ordered.includes(id)) {
        return null;
      }
      ordered.push(id);
      next = targets(id);
    }
    return { ordered, next };
  };
  const branches = anchors("branch");
  const sharedIds = new Set(
    pipeline
      .filter((node) => node.branchId === undefined)
      .map((node) => node.id)
  );
  if (branches.length === 0) {
    const row = walk(targets(start.id), sharedIds);
    if (
      !row ||
      row.ordered.length !== pipeline.length ||
      row.next.length !== 1 ||
      row.next[0] !== output.id
    ) {
      return base;
    }
    if (fallbacks.some((edge) => edge.to !== terminal.id)) {
      return base;
    }
    return {
      ...base,
      mode: "branchless",
      canAnimate: true,
      tailNodeIds: row.ordered,
    };
  }
  const lead = walk(targets(start.id), sharedIds);
  if (
    !lead ||
    lead.next.length === 0 ||
    lead.next.some((id) => nodesById.get(id)?.kind !== "branch")
  ) {
    return base;
  }
  const fallbackByBranchId = new Map<string, RuleGraphEdge>();
  for (const branch of branches) {
    const outgoing = fallbacks.filter(
      (edge) =>
        edge.from === branch.id ||
        nodesById.get(edge.from)?.branchId === branch.id
    );
    const destinations = new Set(outgoing.map((edge) => edge.to));
    if (destinations.size !== 1 || !outgoing[0]) {
      return base;
    }
    fallbackByBranchId.set(branch.id, outgoing[0]);
  }
  const laneOrder: string[] = [];
  const paths = new Map<string, string[]>();
  for (const entry of lead.next) {
    const fresh: string[] = [];
    let current: string | undefined = entry;
    let mergeAt: string | undefined;
    while (current !== undefined) {
      if (fresh.includes(current)) {
        return base;
      }
      if (paths.has(current)) {
        mergeAt = current;
        break;
      }
      fresh.push(current);
      const to = fallbackByBranchId.get(current)?.to;
      if (to === terminal.id) {
        current = undefined;
      } else if (to !== undefined && nodesById.get(to)?.kind === "branch") {
        current = to;
      } else {
        return base;
      }
    }
    let path = fresh;
    if (mergeAt === undefined) {
      laneOrder.push(...fresh);
    } else {
      const existing = paths.get(mergeAt)!;
      path = [...fresh, ...existing.slice(existing.indexOf(mergeAt))];
      laneOrder.splice(laneOrder.indexOf(mergeAt), 0, ...fresh);
    }
    for (const id of path) {
      if ((paths.get(id)?.length ?? 0) < path.length) {
        paths.set(id, path);
      }
    }
  }
  if (laneOrder.length !== branches.length) {
    return base;
  }
  const pipelineByBranchId = new Map<string, string[]>();
  const exits = new Set<string>();
  let canAnimate = true;
  for (const id of laneOrder) {
    const members = new Set(
      pipeline.filter((node) => node.branchId === id).map((node) => node.id)
    );
    if (members.size === 0 && targets(id).length === 0) {
      pipelineByBranchId.set(id, []);
      continue;
    }
    const row = analyzeBranchPipeline(targets(id), members, targets);
    if (
      !row ||
      row.ordered.length !== members.size ||
      row.next.length !== 1 ||
      !row.next[0]
    ) {
      return base;
    }
    pipelineByBranchId.set(id, row.ordered);
    canAnimate = canAnimate && row.linear;
    exits.add(row.next[0]);
  }
  if (exits.size !== 1) {
    return base;
  }
  const tailIds = new Set(
    [...sharedIds].filter((id) => !lead.ordered.includes(id))
  );
  const tail = walk([...exits], tailIds);
  if (
    !tail ||
    tail.ordered.length !== tailIds.size ||
    tail.next.length !== 1 ||
    tail.next[0] !== output.id
  ) {
    return base;
  }
  // Shared steps may exit to the terminal, but cannot introduce another ladder.
  if (
    fallbacks.some((edge) => {
      const source = nodesById.get(edge.from)!;
      return (
        source.kind !== "branch" &&
        source.branchId === undefined &&
        edge.to !== terminal.id
      );
    })
  ) {
    return base;
  }
  const priorityByBranchId = new Map<string, number>();
  for (const id of laneOrder) {
    const path = paths.get(id)!;
    if (path.length > 1) {
      priorityByBranchId.set(id, path.indexOf(id) + 1);
    }
  }
  return {
    ...base,
    mode: "branched",
    canAnimate,
    laneOrder,
    priorityByBranchId,
    pipelineByBranchId,
    fallbackByBranchId,
    leadInNodeIds: lead.ordered,
    tailNodeIds: tail.ordered,
  };
}

/** Keep a connected branch DAG together; ELK decides its internal geometry. */
function analyzeBranchPipeline(
  entry: string[],
  members: Set<string>,
  targets: (id: string) => string[]
): { ordered: string[]; next: string[]; linear: boolean } | null {
  if (members.size === 0) {
    return { ordered: [], next: entry, linear: true };
  }
  if (entry.length === 0 || entry.some((id) => !members.has(id))) {
    return null;
  }
  const reached = new Set<string>();
  const exits = new Set<string>();
  const queue = [...entry];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (reached.has(id)) {
      continue;
    }
    reached.add(id);
    const next = targets(id);
    if (next.length === 0) {
      return null;
    }
    for (const target of next) {
      if (members.has(target)) {
        queue.push(target);
      } else {
        exits.add(target);
      }
    }
  }
  if (reached.size !== members.size) {
    return null;
  }
  const degrees = new Map([...members].map((id) => [id, 0]));
  for (const id of members) {
    for (const target of targets(id)) {
      if (members.has(target)) {
        degrees.set(target, degrees.get(target)! + 1);
      }
    }
  }
  const linear =
    entry.length === 1 &&
    [...members].every(
      (id) => targets(id).length === 1 && degrees.get(id)! <= 1
    );
  const ready = [...members].filter((id) => degrees.get(id) === 0).sort();
  const ordered: string[] = [];
  while (ready.length > 0) {
    const id = ready.shift()!;
    ordered.push(id);
    for (const target of targets(id)) {
      if (!members.has(target)) {
        continue;
      }
      const remaining = degrees.get(target)! - 1;
      degrees.set(target, remaining);
      if (remaining === 0) {
        ready.push(target);
        ready.sort();
      }
    }
  }
  return ordered.length === members.size
    ? { ordered, next: [...exits], linear }
    : null;
}
